import axios from "axios";
import moment from "moment";
import { useState } from "react";
import Swal from "sweetalert2";
import { initialBulkInfo } from "../../const";
import { getMinimumStays } from "../getMinimumStays";
import { getMaximumStays } from "../getMaximumStays";
import { formatCurrency } from "../../../../../utils/formatCurrency";

export const useBulkData = ({
  closeout,
  availability,
  minimumStay,
  maximumStay,
  rentPerDayByDate,
  ratePlans,
  rooms,
  setCloseout,
  setAvailability,
  setRentPerDayByDate,
  setMinimumStay,
  setMaximumStay,
  setRatePlans,
  setBulkModalVisible,
}) => {
  const [bulkInfo, setBulkInfo] = useState(initialBulkInfo);

  const resetBulkInfoState = () => {
    setBulkInfo((prev) => ({
      ...initialBulkInfo,
      bulkDateRanges: [{ startDate: null, endDate: null }],
    }));
  };

  // Handle adding a new date range
  const handleAddDateRange = () => {
    setBulkInfo((prev) => ({
      ...prev,
      bulkDateRanges: [
        ...prev.bulkDateRanges,
        { startDate: null, endDate: null },
      ],
    }));
  };

  // Handle removing a date range
  const handleRemoveDateRange = (index) => {
    if (bulkInfo.bulkDateRanges.length === 1) {
      Swal.fire("Error", "At least one date range is required.", "error");
      return;
    }
    setBulkInfo((prev) => ({
      ...prev,
      bulkDateRanges: prev.bulkDateRanges.filter((_, i) => i !== index),
    }));
  };

  // Handle updating a date range
  const handleDateRangeChange = (index, field, value) => {
    const updatedRanges = [...bulkInfo.bulkDateRanges];
    updatedRanges[index][field] = value;
    setBulkInfo((prev) => ({
      ...prev,
      bulkDateRanges: updatedRanges,
    }));
  };

  const handleBulkModalOk = async () => {
    const {
      bulkRoomId,
      bulkRatePlanId,
      bulkDateRanges,
      bulkAvailability,
      bulkRate,
      bulkCloseout,
      bulkMinimumStay,
      bulkMaximumStay,
      bulkRackRate,
      bulkCloseAll,
      bulkOpenAll,
    } = bulkInfo;
    if (!bulkRoomId) {
      Swal.fire("Error", "Please select a room", "error");
      return;
    }

    if (!bulkRatePlanId) {
      Swal.fire("Error", "Please select a rate plan", "error");
      return;
    }

    // Validate date ranges
    const hasInvalidRange = bulkDateRanges.some(
      (range) => !range.startDate || !range.endDate
    );
    if (hasInvalidRange) {
      Swal.fire("Error", "Please fill in all date ranges.", "error");
      return;
    }

    const bulkRateValue = parseInt(bulkRate, 10);
    const bulkRackRateValue = parseInt(bulkRackRate, 10);
    const bulkAvailabilityValue = parseInt(bulkAvailability, 10);

    if (bulkRate && bulkRateValue <= 0) {
      Swal.fire("Invalid Input", "Rate must be greater than 0.", "warning");
      return;
    }

    if (bulkRackRate && bulkRackRateValue <= 0) {
      Swal.fire(
        "Invalid Input",
        "Rack rate must be greater than 0.",
        "warning"
      );
      return;
    }

    // Build a confirmation summary and ask user to confirm before applying
    const roomName =
      (rooms || []).find((r) => r._id === bulkRoomId)?.name || bulkRoomId;
    const ratePlanName =
      (ratePlans[bulkRoomId] || []).find((rp) => rp._id === bulkRatePlanId)
        ?.rateplanname || bulkRatePlanId;

    const rangesText = bulkDateRanges
      .map((r) => `${r.startDate} → ${r.endDate}`)
      .join("<br/>");

    const summaryHtml = `
      <div style="text-align:left">
        <p><strong>Room:</strong> ${roomName}</p>
        <p><strong>Rate plan:</strong> ${ratePlanName}</p>
        <p><strong>Date ranges:</strong><br/>${rangesText}</p>
        <p><strong>Availability:</strong> ${bulkAvailability || "No change"}</p>
        <p><strong>Rate:</strong> ${formatCurrency(bulkRate) || "No change"}</p>
        <p><strong>Min stay:</strong> ${bulkMinimumStay || "No change"}</p>
        <p><strong>Max stay:</strong> ${bulkMaximumStay || "No change"}</p>
        <p><strong>Rack rate:</strong> ${
          formatCurrency(bulkRackRate) || "No change"
        }</p>
        <p><strong>Status:</strong> ${
          bulkCloseout === null || bulkCloseout === undefined
            ? "No Change"
            : bulkCloseout
            ? "Close"
            : "Open"
        }</p>
      </div>
    `;

    const confirmed = await Swal.fire({
      title: "Confirm bulk update",
      html: summaryHtml,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Apply",
      cancelButtonText: "Cancel",
      width: "600px",
    });

    if (!confirmed.isConfirmed) {
      return; // user cancelled
    }

    const updatedAvailabilityByDate = {};
    const updatedRatesByDate = {};
    const updatedCloseoutByDate = {};
    const updatedMinimumStayByDate = {};
    const updatedMaximumStayByDate = {};
    const updatedRackRateByDate = {};

    // Process each date range
    bulkDateRanges.forEach((range) => {
      const start = moment(range.startDate, "YYYY-MM-DD");
      const end = moment(range.endDate, "YYYY-MM-DD");
      const days = end.diff(start, "days");
      for (let i = 0; i <= days; i++) {
        const dateString = moment(range.startDate, "YYYY-MM-DD")
          .add(i, "days")
          .format("DD-MM-YYYY");
        updatedAvailabilityByDate[dateString] =
          bulkAvailabilityValue || availability[bulkRoomId][dateString];
        updatedMinimumStayByDate[dateString] = bulkMinimumStay
          ? getMinimumStays({
              minimumStays: minimumStay[bulkRoomId][dateString],
              selectedRatePlanId: bulkRatePlanId,
              value: bulkMinimumStay,
            })
          : minimumStay[bulkRoomId][dateString];

        updatedMaximumStayByDate[dateString] = bulkMaximumStay
          ? getMaximumStays({
              maximumStays: maximumStay[bulkRoomId][dateString],
              selectedRatePlanId: bulkRatePlanId,
              value: bulkMaximumStay,
            })
          : maximumStay[bulkRoomId][dateString];

        updatedRackRateByDate[dateString] =
          bulkRackRateValue || rentPerDayByDate[bulkRoomId][dateString];
        // Save closeout as object shape to match Room schema: { all: Boolean, ratePlans: [] }
        if (bulkCloseout == null) {
          updatedCloseoutByDate[dateString] =
            closeout[bulkRoomId]?.[dateString];
        } else {
          updatedCloseoutByDate[dateString] = {
            all: bulkAvailabilityValue === 0,
            ratePlans: [],
          };
        }
        // If the user explicitly selected "Closeout all rate plans", override
        if (bulkCloseAll) {
          updatedCloseoutByDate[dateString] = {
            all: true,
            ratePlans: Array.isArray(ratePlans[bulkRoomId])
              ? ratePlans[bulkRoomId].map((plan) => plan._id)
              : [],
          };
        }
        // If the user explicitly selected "Open all rate plans", clear closeouts
        if (bulkOpenAll) {
          updatedCloseoutByDate[dateString] = { all: false, ratePlans: [] };
        }
        if (bulkRatePlanId) {
          const ratePlan = ratePlans[bulkRoomId]?.find(
            (plan) => plan._id === bulkRatePlanId
          );
          if (ratePlan && ratePlan.ratePlanSetup === "Manual") {
            updatedRatesByDate[dateString] =
              bulkRateValue || rentPerDayByDate[bulkRoomId][dateString];
          } else {
            // Swal.fire("Error", "Cannot update derived rates in bulk", "error");
            // return;
          }
        } else {
          updatedRatesByDate[dateString] =
            bulkRateValue || rentPerDayByDate[bulkRoomId][dateString];
        }

        // Handle optional closeout override. Accept boolean (legacy) or object ({ all, ratePlans }).
        if (
          bulkCloseout !== null &&
          bulkCloseout !== undefined &&
          !bulkCloseAll &&
          !bulkOpenAll &&
          (isNaN(bulkAvailabilityValue) || bulkAvailabilityValue > 0)
        ) {
          const previousRatePlans =
            closeout[bulkRoomId]?.[dateString]?.ratePlans || [];

          if (bulkCloseout) {
            // close specific rate plan or all if no ratePlanId provided
            updatedCloseoutByDate[dateString] = bulkRatePlanId
              ? {
                  all: false,
                  ratePlans: previousRatePlans.concat([bulkRatePlanId]),
                }
              : { all: true, ratePlans: [] };
          } else {
            // open: remove ratePlanId from previous list or set all false
            updatedCloseoutByDate[dateString] = {
              all: false,
              ratePlans:
                previousRatePlans.filter((rpId) => rpId !== bulkRatePlanId) ||
                [],
            };
          }
        }
      }
    });

    try {
      // PUT request to update availability, rates, and closeout status
      await axios.put("/api/rooms/updateroomavailability", {
        roomid: bulkRoomId,
        availability: updatedAvailabilityByDate || {},
        rentperdayByDate: updatedRackRateByDate,
        closeout: updatedCloseoutByDate || {},
        minimumStay: updatedMinimumStayByDate,
        maximumStay: updatedMaximumStayByDate,
      });

      setRentPerDayByDate((prev) => ({
        ...prev,
        [bulkRoomId]: {
          ...prev[bulkRoomId],
          ...updatedRackRateByDate,
        },
      }));

      setAvailability((prev) => ({
        ...prev,
        [bulkRoomId]: {
          ...prev[bulkRoomId],
          ...updatedAvailabilityByDate,
        },
      }));

      setMinimumStay((prev) => ({
        ...prev,
        [bulkRoomId]: {
          ...prev[bulkRoomId],
          ...updatedMinimumStayByDate,
        },
      }));

      setMaximumStay((prev) => ({
        ...prev,
        [bulkRoomId]: {
          ...prev[bulkRoomId],
          ...updatedMaximumStayByDate,
        },
      }));

      // Update closeout state if we have any closeout changes
      if (Object.keys(updatedCloseoutByDate).length > 0) {
        setCloseout((prev) => ({
          ...prev,
          [bulkRoomId]: {
            ...prev[bulkRoomId],
            ...updatedCloseoutByDate,
          },
        }));
      }

      // Update the state with the new availability, rates, and closeout statuses
      if (
        bulkRatePlanId &&
        ratePlans[bulkRoomId]?.find((plan) => plan._id === bulkRatePlanId)
          ?.ratePlanSetup === "Manual"
      ) {
        await axios.put("/api/rateplans/updaterateplan", {
          ratePlanId: bulkRatePlanId,
          roomId: bulkRoomId,
          manualRate: updatedRatesByDate,
        });

        setRatePlans((prevRatePlans) => {
          const updatedRatePlans = { ...prevRatePlans };
          const updatedPlan = updatedRatePlans[bulkRoomId].find(
            (plan) => plan._id === bulkRatePlanId
          );
          const updatedPricing = updatedPlan.rateplanpricing.find(
            (rpp) => rpp.roomId.toString() === bulkRoomId
          );
          updatedPricing.manualRate = {
            ...updatedPricing.manualRate,
            ...updatedRatesByDate,
          };
          return updatedRatePlans;
        });
      }

      Swal.fire("Success", "Bulk update successful!", "success");
      setBulkModalVisible(false);
    } catch (error) {
      console.error("Error applying bulk update:", error.message);
      Swal.fire("Error", "Failed to apply bulk update", "error");
    }
  };

  const handleBulkModalCancel = () => {
    resetBulkInfoState();
    setBulkModalVisible(false);
  };

  // Apply closeout for a single room/date. Keeps closeout persistence centralized.
  const applyCloseout = async (roomId, dateKey, allFlag, ratePlanIds = []) => {
    try {
      const payload = {
        [dateKey]: { all: !!allFlag, ratePlans: ratePlanIds },
      };

      await axios.put("/api/rooms/updateroomavailability", {
        roomid: roomId,
        closeout: payload,
      });

      setCloseout((prev) => ({
        ...prev,
        [roomId]: {
          ...prev[roomId],
          [dateKey]: payload[dateKey],
        },
      }));

      Swal.fire("Success", "Closeout updated successfully", "success");
      return true;
    } catch (err) {
      console.error("Error applying closeout:", err.message || err);
      Swal.fire("Error", "Failed to update closeout", "error");
      return false;
    }
  };

  return {
    bulkInfo,
    setBulkInfo,
    handleAddDateRange,
    handleRemoveDateRange,
    handleDateRangeChange,
    handleBulkModalOk,
    handleBulkModalCancel,
    applyCloseout,
  };
};
